import type { PaginationMeta } from './common';
import type { ApiResponse, ExtraFields } from './generic';

//DocumentRow type with specific status values
export interface DocumentRow {
  id: string;
  type: string;
  uploadDate: string;
  url: string;
}

export interface DocumentType {
  id: string;
  companyId: string;
  name: string;
  status: boolean;
  description: string;
  extraData: ExtraFields | null;
  createdByUserId: string;
  updatedByUserId: string | null;
  createdAt: string;
  updatedAt: string | null;
  deletedAt: string | null;
}

/**
 * Active document types of the logged-in employee's own company, for the
 * picker on a document change request — the employee-scoped counterpart of the
 * admin-only company document-type list.
 */
export type GetEmployeeDocumentTypesResponse = ApiResponse<DocumentType[]>;

export interface EmployeeDocument {
  id: string;
  employeeId: string;
  companyId: string;
  documentTypeId: string;
  status: DocumentStatus;
  documentUrl: string;
  /** The day the document stops being valid (`YYYY-MM-DD`), when HR or the employee set one. */
  expiresOn?: string | null;
  extraData: ExtraFields | null;
  createdByUserId: string;
  updatedByUserId: string | null;
  createdAt: string;
  updatedAt: string | null;
  deletedAt: string | null;
  documentType: DocumentType;
}

export interface Meta {
  page: number;
  limit: number;
  totalCount: number;
  lastPage: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface EmployeeDocumentsData {
  data: EmployeeDocument[];
  meta: Meta;
}

export interface EmployeeDocumentsResponse {
  message: string;
  data: EmployeeDocumentsData;
}

export enum DocumentStatus {
  Approved = 'approved',
  Pending = 'pending',
  Rejected = 'rejected',
}

/**
 * Where an HR document request stands (mirror of the backend
 * `document-requests` enum). Overdue is not a status: an open request past
 * its due date carries `overdue: true`.
 */
export enum DocumentRequestStatus {
  Open = 'open',
  Fulfilled = 'fulfilled',
  Cancelled = 'cancelled',
}

/**
 * HR asking one employee for one document type by a due date. The same shape
 * in both portals: `GET companies/:companyId/employees/:employeeId/document-requests`
 * (HR) and `GET employee/document-requests` (the employee's own).
 */
export interface DocumentRequest {
  id: string;
  employeeId: string;
  documentType: { id: string; name: string; hasExpiry: boolean };
  note: string | null;
  /** Company-local `YYYY-MM-DD`. */
  dueOn: string;
  status: DocumentRequestStatus;
  /** Open and past its due date in the company's time zone. */
  overdue: boolean;
  /** Open only: days until due (negative when overdue), else null. */
  daysLeft: number | null;
  requestedBy: { id: string; name: string };
  createdAt: string;
  fulfilledAt: string | null;
  fulfilledDocumentId: string | null;
  cancelledAt: string | null;
}

/** A request in HR's company-wide list: the request plus who it was sent to. */
export interface CompanyDocumentRequest extends DocumentRequest {
  employee: { id: string; name: string };
}

/** `GET companies/:companyId/document-requests` (HR, paged). */
export type CompanyDocumentRequestsResponse = ApiResponse<{
  data: CompanyDocumentRequest[];
  meta: PaginationMeta;
}>;

export type DocumentRequestsResponse = ApiResponse<DocumentRequest[]>;
export type DocumentRequestResponse = ApiResponse<DocumentRequest>;

/** HR sends a request. */
export interface CreateDocumentRequestPayload {
  documentTypeId: string;
  /** `YYYY-MM-DD`, today or later (company-local). */
  dueOn: string;
  note?: string;
}

/** HR moves the due date or edits the note of an open request. */
export interface UpdateDocumentRequestPayload {
  dueOn?: string;
  /** `null` clears the note. */
  note?: string | null;
}

/** The employee's upload against a request (already in S3). */
export interface FulfilDocumentRequestPayload {
  documentUrl: string;
  expiresOn?: string;
}
